/**
 * WEB-1532 — потолок числа вариантов ответа в одном блоке.
 *
 * ИСТОЧНИК ПРАВДЫ — БЭКЕНД: `backend/src/ai-funnels/block-schema/questions.ts`,
 * где `.max(30)` стоит в ПЯТИ местах одной семьи полей:
 *   - `singleChoiceSchema.options`
 *   - `multipleChoiceSchema.options`
 *   - `multiStepQuestionSchema.options`
 *   - `selectDropdownSchema.selectOptions`
 *   - `imageChoiceSchema.options`
 * Границу сервера этой карточкой НЕ меняем — интерфейс обязан её повторить.
 *
 * Зачем копия числа на фронте: панель должна знать потолок ДО сохранения.
 * Иначе тридцать первый вариант спокойно добавляется, а потом весь контент
 * воронки отбивается 400-й (`content-write-guard.ts` проверяет содержимое
 * целиком) — не сохраняются правки на ВСЕХ экранах, и человек не понимает,
 * почему. Сгенерированные типы (`src/api/generated/schema.ts`) числовых
 * ограничений не переносят, поэтому взять число из них нельзя.
 *
 * Копия не остаётся без присмотра: сторож
 * `backend/src/ai-funnels/block-schema/block-options-cap-fe-mirror.web1532.spec.ts`
 * читает этот файл с диска и краснеет при любом расхождении с любым из пяти
 * серверных мест, а заодно проверяет, что панель и хранилище редактора
 * действительно ссылаются на эту константу, а не держат своё число.
 */
/**
 * WEB-1874 (пачка 15): подсказки у заглохших кнопок — надписи КАБИНЕТА, а
 * значит живут в словаре. Здесь остаются ЧИСЛА (зеркало серверных схем) и
 * имена констант: их сторожа `block-options-cap-fe-mirror.web1532.spec.ts` и
 * `block-list-cap-web1687.spec.ts` ищут в исходниках обеих панелей, поэтому
 * имя менять нельзя — меняется только форма значения (строка → функция без
 * аргументов, читающая язык в момент показа). Словарь панели расширенных
 * свойств выбран не по красоте, а по факту: эти шесть подсказок рисует
 * главным образом она, а вторая панель берёт ту же константу.
 */
import { pickStrings } from '@/i18n';
import { editorPanelExtendedStrings } from '@/i18n/dictionaries/editorPanelExtended';

export const MAX_BLOCK_OPTIONS = 30;

/** Подсказка автору у заглохшей кнопки «Добавить вариант». */
export const MAX_BLOCK_OPTIONS_HINT = (): string =>
  pickStrings(editorPanelExtendedStrings).maxBlockOptionsHint(
    MAX_BLOCK_OPTIONS,
  );

/**
 * WEB-1687 — ПОТОЛКИ ОСТАЛЬНЫХ РЕДАКТИРУЕМЫХ СПИСКОВ.
 *
 * Источник правды тот же — серверные Zod-схемы:
 *   · `content.ts` → `loaderProcessingSchema.steps/questions/socialProof`;
 *   · `paywall.ts` → `planSelectorSchema.plans`.
 * Числа здесь их ПОВТОРЯЮТ (менять границу сервера карточка не должна), и
 * сверяет повтор сторож `block-list-cap-web1687.spec.ts`: он выводит список
 * мест обходом самой схемы, поэтому расхождение красит поимённо.
 */
export const MAX_LOADER_STEPS = 20;
export const MAX_LOADER_QUESTIONS = 20;
export const MAX_LOADER_SOCIAL_PROOF = 30;
export const MAX_PLAN_SELECTOR_PLANS = 12;
/**
 * WEB-1829 — потолок числа плашек-фич. Источник правды тот же: `content.ts` →
 * `featureChipsSchema.featureChipItems.max(12)`. Копия нужна ровно затем же,
 * зачем остальные: без неё тринадцатая плашка спокойно добавляется в панели, а
 * потом 400-й отбивается контент ВСЕЙ воронки, и автор не понимает, почему
 * перестали сохраняться правки на всех экранах.
 */
export const MAX_FEATURE_CHIPS = 12;
/**
 * WEB-1362 п.8 — длина подписи-пилюли поверх снимка. Источник правды тот же:
 * `content.ts` → `captionChip: z.string().max(120)`.
 *
 * Копия нужна ровно затем же, зачем остальные в этом файле: панель обязана
 * поймать границу ДО сохранения. Иначе сто двадцать первый символ спокойно
 * вводится, а потом отбивается контент ВСЕЙ воронки (`content-write-guard.ts`
 * проверяет содержимое целиком), человек видит только «Не сохранено —
 * повторить» и ищет виновное поле методом тыка — пока не найдёт, не
 * сохраняются НИКАКИЕ правки на ВСЕХ экранах.
 *
 * Под присмотром сторожа двух концов
 * `captionChipLength.twoEnds.web1362.guard.test.ts`: он читает схему сервера с
 * диска и краснеет при расхождении, а заодно требует, чтобы панель ссылалась
 * на эту константу, а не писала своё число.
 */
export const MAX_CAPTION_CHIP_LENGTH = 120;

/** Подсказки автору у заглохших кнопок добавления. */
export const MAX_LOADER_STEPS_HINT = (): string =>
  pickStrings(editorPanelExtendedStrings).maxLoaderStepsHint(MAX_LOADER_STEPS);
export const MAX_LOADER_QUESTIONS_HINT = (): string =>
  pickStrings(editorPanelExtendedStrings).maxLoaderQuestionsHint(
    MAX_LOADER_QUESTIONS,
  );
export const MAX_LOADER_SOCIAL_PROOF_HINT = (): string =>
  pickStrings(editorPanelExtendedStrings).maxLoaderSocialProofHint(
    MAX_LOADER_SOCIAL_PROOF,
  );
export const MAX_PLAN_SELECTOR_PLANS_HINT = (): string =>
  pickStrings(editorPanelExtendedStrings).maxPlanSelectorPlansHint(
    MAX_PLAN_SELECTOR_PLANS,
  );
export const MAX_FEATURE_CHIPS_HINT = (): string =>
  pickStrings(editorPanelExtendedStrings).maxFeatureChipsHint(
    MAX_FEATURE_CHIPS,
  );

/** Одно поле-список: имя РЕДАКТОРА, его потолок и подсказка у кнопки. */
export interface BlockListFieldLimit {
  /**
   * Имя поля в том виде, в каком его пишет редактор (`EditorBlock`). У части
   * полей две номенклатуры, и здесь стоит именно РЕДАКТОРСКОЕ имя: у
   * картиночного выбора это `imageChoiceOptions`, а не `options` из серверной
   * схемы; у шагов лоадера — `loaderSteps`, а не `steps`. Пары объявлены в
   * `backend/src/ai-funnels/block-schema/field-name-registry.ts`.
   */
  readonly field: string;
  /** Потолок из серверной схемы — повтор, а не собственное число. */
  readonly max: number;
  /**
   * Что показать автору у заглохшей кнопки добавления. ФУНКЦИЯ, а не строка:
   * язык кабинета известен только в момент показа (WEB-1874).
   */
  readonly hint: () => string;
}

/**
 * Ревизия пачек 7-8 (находка 1) + WEB-1687: ПОД КАКИМ ИМЕНЕМ И С КАКИМ
 * ПОТОЛКОМ каждое семейство блоков хранит свои редактируемые списки.
 *
 * Пока список жил только в комментарии, вторая дверь стерегла ровно то поле,
 * которое знал редьюсер `addBlockOption` — `options`, то есть три семейства
 * из пяти. Выпадающий список и картиночный выбор пишут свои массивы мимо
 * него, через общий `updateBlock`, и потолок на них не действовал (WEB-1532).
 *
 * WEB-1687 расширил реестр с «одно поле вариантов на тип блока» до «сколько
 * угодно полей-списков со СВОИМ потолком у каждого»: у экрана загрузки их
 * сразу три (шаги, вопросы, строки отзывов) и числа разные. Отдельный второй
 * реестр завёл бы вторую правду, которая разъедется при следующем поле, —
 * поэтому семейство `options` живёт здесь же.
 *
 * Имена сверяются с серверными через реестр пар: сторожа
 * `block-options-cap-fe-mirror.web1532.spec.ts` и
 * `block-list-cap-web1687.spec.ts` читают этот файл с диска и краснеют, если
 * имя или число разъедется со схемой, — иначе дверь стережёт пустоту.
 */
export const BLOCK_LIST_FIELD_LIMITS_BY_TYPE: Readonly<
  Record<string, readonly BlockListFieldLimit[]>
> = {
  single_choice: [
    { field: 'options', max: MAX_BLOCK_OPTIONS, hint: MAX_BLOCK_OPTIONS_HINT },
  ],
  multiple_choice: [
    { field: 'options', max: MAX_BLOCK_OPTIONS, hint: MAX_BLOCK_OPTIONS_HINT },
  ],
  multi_step_question: [
    { field: 'options', max: MAX_BLOCK_OPTIONS, hint: MAX_BLOCK_OPTIONS_HINT },
  ],
  select_dropdown: [
    {
      field: 'selectOptions',
      max: MAX_BLOCK_OPTIONS,
      hint: MAX_BLOCK_OPTIONS_HINT,
    },
  ],
  image_choice: [
    {
      field: 'imageChoiceOptions',
      max: MAX_BLOCK_OPTIONS,
      hint: MAX_BLOCK_OPTIONS_HINT,
    },
  ],
  loader_processing: [
    { field: 'loaderSteps', max: MAX_LOADER_STEPS, hint: MAX_LOADER_STEPS_HINT },
    {
      field: 'loaderQuestions',
      max: MAX_LOADER_QUESTIONS,
      hint: MAX_LOADER_QUESTIONS_HINT,
    },
    {
      field: 'loaderSocialProof',
      max: MAX_LOADER_SOCIAL_PROOF,
      hint: MAX_LOADER_SOCIAL_PROOF_HINT,
    },
  ],
  plan_selector: [
    {
      field: 'planSelectorPlans',
      max: MAX_PLAN_SELECTOR_PLANS,
      hint: MAX_PLAN_SELECTOR_PLANS_HINT,
    },
  ],
  // WEB-1829: плашки-фичи. Имя поля одно и то же у схемы и у редактора —
  // пары в field-name-registry не нужно.
  feature_chips: [
    {
      field: 'featureChipItems',
      max: MAX_FEATURE_CHIPS,
      hint: MAX_FEATURE_CHIPS_HINT,
    },
  ],
};
